import { cos, dot, Fn, float, floor, max, sin, sqrt, step, uvec3, vec3, vec4 } from 'three/tsl';

/**
 * 3D gradient noise with analytic derivatives (after Inigo Quilez), periodic
 * with 289 lattice cells so tile-local lattice offsets can be reduced modulo
 * the period on the CPU in f64. Returns vec4(value, d/dx, d/dy, d/dz) in
 * lattice units; value is roughly in [-1, 1].
 */
const PERIOD = 289;

type N = any;

const wrap = (i: N) => i.sub(floor(i.div(PERIOD)).mul(PERIOD));

/** PCG3D integer hash → gradient vector in [-1, 1]³. */
export const hashGrad = Fn(([c]: [N]) => {
  const v: N = uvec3(c).toVar();
  v.assign(v.mul(uvec3(1664525)).add(uvec3(1013904223)));
  v.x.addAssign(v.y.mul(v.z));
  v.y.addAssign(v.z.mul(v.x));
  v.z.addAssign(v.x.mul(v.y));
  v.assign(v.bitXor(v.shiftRight(uvec3(16))));
  v.x.addAssign(v.y.mul(v.z));
  v.y.addAssign(v.z.mul(v.x));
  v.z.addAssign(v.x.mul(v.y));
  return vec3(v)
    .mul(2.0 / 4294967295.0)
    .sub(1.0);
}).setLayout({ name: 'hashGrad', type: 'vec3', inputs: [{ name: 'c', type: 'vec3' }] });

export const gnoised = Fn(([x]: [N]) => {
  const i: N = floor(x);
  const f: N = x.sub(i);
  const u: N = f
    .mul(f)
    .mul(f)
    .mul(f.mul(f.mul(6.0).sub(15.0)).add(10.0));
  const du: N = f
    .mul(f)
    .mul(30.0)
    .mul(f.mul(f.sub(2.0)).add(1.0));
  const i0 = wrap(i);
  const i1 = wrap(i.add(1.0));
  const ga = hashGrad(vec3(i0.x, i0.y, i0.z));
  const gb = hashGrad(vec3(i1.x, i0.y, i0.z));
  const gc = hashGrad(vec3(i0.x, i1.y, i0.z));
  const gd = hashGrad(vec3(i1.x, i1.y, i0.z));
  const ge = hashGrad(vec3(i0.x, i0.y, i1.z));
  const gf = hashGrad(vec3(i1.x, i0.y, i1.z));
  const gg = hashGrad(vec3(i0.x, i1.y, i1.z));
  const gh = hashGrad(vec3(i1.x, i1.y, i1.z));
  const va = dot(ga, f);
  const vb = dot(gb, f.sub(vec3(1, 0, 0)));
  const vc = dot(gc, f.sub(vec3(0, 1, 0)));
  const vd = dot(gd, f.sub(vec3(1, 1, 0)));
  const ve = dot(ge, f.sub(vec3(0, 0, 1)));
  const vf = dot(gf, f.sub(vec3(1, 0, 1)));
  const vg = dot(gg, f.sub(vec3(0, 1, 1)));
  const vh = dot(gh, f.sub(vec3(1, 1, 1)));

  const k0 = va.sub(vb).sub(vc).add(vd);
  const k1 = va.sub(vc).sub(ve).add(vg);
  const k2 = va.sub(vb).sub(ve).add(vf);
  const k3 = va.negate().add(vb).add(vc).sub(vd).add(ve).sub(vf).sub(vg).add(vh);

  const value: N = va
    .add(u.x.mul(vb.sub(va)))
    .add(u.y.mul(vc.sub(va)))
    .add(u.z.mul(ve.sub(va)))
    .add(u.x.mul(u.y).mul(k0))
    .add(u.y.mul(u.z).mul(k1))
    .add(u.z.mul(u.x).mul(k2))
    .add(k3.mul(u.x).mul(u.y).mul(u.z));

  const g0 = ga.sub(gb).sub(gc).add(gd);
  const g1 = ga.sub(gc).sub(ge).add(gg);
  const g2 = ga.sub(gb).sub(ge).add(gf);
  const g3 = ga.negate().add(gb).add(gc).sub(gd).add(ge).sub(gf).sub(gg).add(gh);
  const deriv: N = ga
    .add(gb.sub(ga).mul(u.x))
    .add(gc.sub(ga).mul(u.y))
    .add(ge.sub(ga).mul(u.z))
    .add(g0.mul(u.x.mul(u.y)))
    .add(g1.mul(u.y.mul(u.z)))
    .add(g2.mul(u.z.mul(u.x)))
    .add(g3.mul(u.x.mul(u.y).mul(u.z)))
    .add(
      du.mul(
        vec3(vb.sub(va), vc.sub(va), ve.sub(va))
          .add(u.yzx.mul(vec3(k0, k1, k2)))
          .add(u.zxy.mul(vec3(k2, k0, k1)))
          .add(u.yzx.mul(u.zxy).mul(k3)),
      ),
    );
  return vec4(value as N, deriv as N);
}).setLayout({ name: 'gnoised', type: 'vec4', inputs: [{ name: 'x', type: 'vec3' }] });

export const NOISE_PERIOD = PERIOD;

/** Added to stripe cells before hashing, so their phases don't repeat gnoised's gradients. */
export const STRIPE_SALT: [number, number, number] = [131, 57, 211];
/** Where the stripes' blended phasors cancel below this, they fade out (a phase singularity). */
export const STRIPE_FLOOR = 0.3;
const TAU = 2 * Math.PI;

/**
 * Stripes along unit direction `w`: parallel waves, one lattice unit apart, each cell corner
 * shifting their phase by its own random amount. The corners' waves are blended as phasors
 * (cosine and sine) and the result normalised, so the stripes keep full contrast and only bend,
 * fork and end where the corners disagree (the idea of phasor and "phacelle" noise, in 3D).
 * Where the phasors cancel, it fades to 0 instead of jumping. Periodic like gnoised (cells wrap
 * every 289), so tile-local lattice offsets work the same way. Returns vec4(value in [−1, 1],
 * d/dx, d/dy, d/dz) in lattice units, `w` taken as constant.
 */
export const stripesd = Fn(([x, w]: [N, N]) => {
  const i: N = floor(x);
  const f: N = x.sub(i);
  const u: N = f
    .mul(f)
    .mul(f)
    .mul(f.mul(f.mul(6.0).sub(15.0)).add(10.0));
  const du: N = f
    .mul(f)
    .mul(30.0)
    .mul(f.mul(f.sub(2.0)).add(1.0));
  const C: N = float(0).toVar();
  const S: N = float(0).toVar();
  const dC: N = vec3(0).toVar();
  const dS: N = vec3(0).toVar();
  const salt = vec3(...STRIPE_SALT);
  for (const cz of [0, 1]) {
    for (const cy of [0, 1]) {
      for (const cx of [0, 1]) {
        const o = vec3(cx, cy, cz);
        const phase = hashGrad(wrap(i.add(o)).add(salt)).x;
        const t = dot(f.sub(o), w).add(phase).mul(TAU);
        // The corner's trilinear (quintic) weight and its gradient.
        const wx = cx ? u.x : float(1).sub(u.x);
        const wy = cy ? u.y : float(1).sub(u.y);
        const wz = cz ? u.z : float(1).sub(u.z);
        const dwx = cx ? du.x : du.x.negate();
        const dwy = cy ? du.y : du.y.negate();
        const dwz = cz ? du.z : du.z.negate();
        const wt = wx.mul(wy).mul(wz);
        const dwt = vec3(dwx.mul(wy).mul(wz), wx.mul(dwy).mul(wz), wx.mul(wy).mul(dwz));
        const c = cos(t);
        const s = sin(t);
        C.addAssign(wt.mul(c));
        S.addAssign(wt.mul(s));
        dC.addAssign(dwt.mul(c).sub(w.mul(wt.mul(s).mul(TAU))));
        dS.addAssign(dwt.mul(s).add(w.mul(wt.mul(c).mul(TAU))));
      }
    }
  }
  // C / max(|phasor|, floor): the cosine of the blended phase, fading where the phasor is weak.
  const m = sqrt(C.mul(C).add(S.mul(S)));
  const inv = float(1).div(max(m, STRIPE_FLOOR));
  const dm = C.mul(dC).add(S.mul(dS)).div(max(m, 1e-6));
  const grad = dC.mul(inv).sub(dm.mul(C.mul(inv).mul(inv)).mul(step(STRIPE_FLOOR, m)));
  return vec4(C.mul(inv) as N, grad as N);
}).setLayout({
  name: 'stripesd',
  type: 'vec4',
  inputs: [
    { name: 'x', type: 'vec3' },
    { name: 'w', type: 'vec3' },
  ],
});
